import { type CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';

import { type MessageSource } from './types';

/**
 * Request-side markers attached to the aiMateSendMessage experience so the
 * REMIX_CREATE compound experience (via createRemixCreateCollect) can correlate a
 * generation-stream failure back to the remix-create flow.
 *
 * Mirrors getRemixRegenerateRequestAttributes. The create flow is a first-
 * generation remix send (creationContextParams.source === 'remix'); regenerate is
 * distinguished by messageSources.interactionSource === 'inlineRovoFromRemix'.
 */
export type RemixCreateRequestAttributes = {
	isRemixCreate?: true;
	contentMauiId?: string;
	mediaFileId?: string;
	remixSubtype?: string;
	remixType?: string;
	remixExperience?: CreationContextParams['experience'];
};

export const getRemixCreateRequestAttributes = ({
	creationContextParams,
	messageSources,
}: {
	creationContextParams?: CreationContextParams;
	messageSources?: Partial<MessageSource>;
}): RemixCreateRequestAttributes => {
	// A remix-create send originates from the remix menu (source === 'remix') and
	// is NOT a regenerate of an existing node (which uses inlineRovoFromRemix).
	const isRemixCreate =
		creationContextParams?.source === 'remix' &&
		messageSources?.interactionSource !== 'inlineRovoFromRemix';

	if (!isRemixCreate) {
		return {};
	}

	return {
		isRemixCreate: true,
		contentMauiId: creationContextParams?.contentMauiId,
		mediaFileId: creationContextParams?.mediaFileId,
		remixType: creationContextParams?.dynamicUiType,
		remixSubtype: creationContextParams?.dynamicUiSubtype,
		...(creationContextParams?.experience
			? { remixExperience: creationContextParams.experience }
			: {}),
	};
};
